'use client'

import { useEffect } from 'react'
import { trackException } from '@/src/lib/appInsights'
import { logger } from '@/src/lib/logger'

/**
 * Global Error Boundary (Next.js App Router).
 *
 * Captura erros que quebram o próprio root layout (raros, mas catastróficos).
 * Substitui o root layout inteiro — por isso renderiza <html> e <body>.
 *
 * Para erros dentro de rotas individuais (com root layout funcional),
 * ver `error.tsx`.
 *
 * Task #193 / F13.6.
 */

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    trackException(error, {
      source: 'GlobalError',
      digest: error.digest ?? '',
      url: typeof window !== 'undefined' ? window.location.href : '',
    })
    logger.error('Next.js global error caught (root layout broken)', error, {
      digest: error.digest ?? '',
    })
  }, [error])

  return (
    <html lang="pt-BR">
      <body
        style={{
          margin: 0,
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          background: '#f9fafb',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
        }}
      >
        <div
          role="alert"
          style={{
            maxWidth: '28rem',
            width: '100%',
            background: '#ffffff',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ fontSize: '2.25rem', color: '#dc2626', marginBottom: '0.75rem' }}>
            ⚠
          </div>
          <h1
            style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#b91c1c',
              margin: '0 0 0.5rem',
            }}
          >
            Erro crítico no Portal CONECTA
          </h1>
          <p style={{ fontSize: '0.875rem', color: '#4b5563', margin: '0 0 1.25rem' }}>
            Ocorreu um erro inesperado que impediu o carregamento da aplicação. Nossa equipe
            já foi notificada. Por favor, tente novamente — se o problema persistir, recarregue
            a página ou contate o suporte.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#7E22CE',
              color: '#ffffff',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Tentar novamente
          </button>
        </div>
      </body>
    </html>
  )
}
